// This is a COPY of downread/shared/src/schema/api-schema.ts. To modify, edit the original and then run downread/bin/build-shared.sh
// Constants ------------------------------

import { FeedCategory, PostMeta, PostStats, PostTextType, PreviewMode, RssType } from "./feed-schema";

/** Original format of a stored feed image. 'svg' = vector; the rest are bitmaps served as scaled
 * WebP. Mirrors the same union in db-schema/feed-reader; declared locally to avoid import cycles. */
export type FeedImageExt = 'svg' | 'png' | 'ico' | 'jpg' | 'webp' | 'gif';

/** Which image a feed uses as its default icon: auto-fetched favicon, RSS feed image, an
 * admin-uploaded custom image, or an auto-generated fallback icon. */
export type ImageDefault = 'favicon' | 'feedImage' | 'custom' | 'fallback';

/** CDN a feed's post files are served from. Mirrors FeedCdn in utils/feed-cdn; declared locally
 * because the frontend's schema-copy of this file can't reach outside the copied directory. */
export type FeedCdn = 'us' | 'eu';

/** Maximum number of active posts to track in database */
export const MAX_ACTIVE_POSTS = 1000;

/**
 * How many items of a single fetched feed document we process. Everything beyond this is
 * discarded, so the feed-reader applies the same cap before sanitizing rather than paying
 * to sanitize items the sync-server is about to throw away.
 *
 * A feed we already have posts for only needs enough items to spot what changed; a feed we
 * are seeing for the first time gets a much higher cap so its archive is imported.
 */
export const MAX_FETCHED_ITEMS_UPDATE = 200;
export const MAX_FETCHED_ITEMS_INITIAL = 1200;

/** The cap that applies to a feed, given whether we already have posts for it. */
export function fetchedItemLimit(hasPosts: boolean): number {
    return hasPosts ? MAX_FETCHED_ITEMS_UPDATE : MAX_FETCHED_ITEMS_INITIAL;
}

/**
 * Newest-first comparator for fetched items, matching the order the sync-server applies
 * before capping. Items without a usable date sort oldest, so they are dropped first.
 */
export function compareItemsNewestFirst(a: { pubDate?: Date | string | null }, b: { pubDate?: Date | string | null }): number {
    const ms = (d?: Date | string | null) => d ? (d instanceof Date ? d.getTime() : Date.parse(d)) || 0 : 0;
    return ms(b.pubDate) - ms(a.pubDate);
}

/** Max starred and read-later posts per user (each list separately). Enforced client-side with a
 *  refusal message; the server additionally trims to the newest entries as a safety net. */
export const MAX_SAVED_POSTS = 1000;

/** Max post-times (recentPostsSemi) shipped per feed to anonymous (no-login) clients. The client
 *  builds its unread list locally from these, so it can place at most this many newest posts per
 *  feed in the timeline — past that the user has "reached the end" of the unread list. */
export const ANON_SEMI_CAP = 100;

// Generic list response with pagination
export interface RestList<T> {
    items: T[];
    hasMore: boolean;
    cursor?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}

// Users ------------------------------

// GET /api/users/:id
// GET /api/users/email/:email  (superuser only!)
export interface RestUser {
    /** Unique identifier for the user */
    id: string;
    /** Not really used yet, but the auth server has it. Right now every user is her own team. */
    teamId: string;
    /** Optional tag for marking test data */
    tag?: string;
    /** External account identifier for auth service */
    accountId: string;
    /** List of feed IDs the user is subscribed to */
    feeds: string[];
    /** Display name for the user */
    nickName: string;
    /** User's email address */
    email?: string;
    /** URL to the user's avatar image */
    avatarUrl?: string
    /** Timestamp of the last update to the user record (ISO 8601 string) */
    lastUpdate: string;
    /** Current status of the user account (e.g., active, suspended) */
    userStatus: 'active' | 'suspended' | 'deleted';
    /** Current plan of the user account */
    userPlan: 'free' | 'pro' | 'vip' | 'earlyAdopter' | 'test';
    /** Timestamp until which the user has paid (ISO 8601 string) */
    userPaidUntil?: string;
    /** Preferences */
    preferences: UserPreferences;
    /** Timestamp when the user was created (ISO 8601 string) */
    createdAt: string;
}


// POST /api/user
export type RestUserCreateRequest = Pick<RestUser, 'nickName' | 'tag'>

// PUT /api/user/self
export type RestUserSelfUpdateRequest = Partial<RestUserCreateRequest>;

// PUT /api/user/:id
export type RestUserUpdateRequest = RestUserSelfUpdateRequest & Partial<Pick<RestUser, 'avatarUrl' | 'userStatus' | 'userPlan' | 'userPaidUntil'>>;

// DELETE /api/user/self/:id


// User preferences ------------------------------

/** Post field a filter keyword is checked against; 'all' checks every field. */
export type FeedFilterField = 'title' | 'body' | 'author' | 'url' | 'all';

/** How a filter keyword is compared against a field. Always case-insensitive.
 *  The not* variants invert the whole keyword: with field 'all', 'notContains' means
 *  NO field contains the text. A post matching the (combined) filter is marked read,
 *  so e.g. a single notContains keyword filters out every post without the text. */
export type FeedFilterOp = 'contains' | 'notContains' | 'startsWith' | 'notStartsWith' | 'endsWith' | 'notEndsWith';

export interface FeedFilterKeyword {
    /** Keyword text, compared case-insensitively (1-80 chars, trimmed). */
    text: string;
    field: FeedFilterField;
    /** Comparison operator; default is 'contains'. */
    op?: FeedFilterOp;
}

/** Per-feed keyword filter. Matching posts are automatically marked as read (evaluated purely
 * client-side; available on plans with PlanLimits.keywordFilters). */
export interface FeedFilter {
    /** 'any' = a post matching at least one keyword is filtered (OR);
     *  'all' = a post must match every keyword (AND). */
    match: 'any' | 'all';
    /** 1-10 keywords. */
    keywords: FeedFilterKeyword[];
}

/** Third-party services whose embeds require user consent before the browser contacts them. */
export const EMBED_SERVICES = ['youtube', 'vimeo', 'jwplayer', 'simplecast', 'captivate', 'x', 'instagram', 'tiktok', 'threads', 'reddit', 'bluesky'] as const;
export type EmbedService = typeof EMBED_SERVICES[number];

export interface UserPreferences {
    /** Last selected feed list type in admin page */
    adminFeedListType?: 'new' | 'unverified' | 'popular' | 'problem' | 'bad';
    /** Last search filter in admin page */
    adminFeedSearchName?: string;
    /** Last search filter in admin catalogs page */
    adminCatalogSearchName?: string;

    /** Color theme. 'auto' follows the OS/browser preference. Default is 'auto' in the native apps, 'light' on the web. */
    theme?: 'light' | 'dark' | 'auto';

    // Post preferences
    /** Preferred layout for displaying posts */
    preferredLayout?: '2col' | '1col' | 'list';
    /** Font family for displaying post content. Default is 'serif'. */
    postFont?: 'sans-serif' | 'serif';
    /** Post body font size in px (10-20, default 16). Title renders 6px larger, byline 2px smaller. */
    postFontSize?: number;
    /** Max width of the post column in px (500-1000 in steps of 100, default 700). */
    postWidth?: number;
    /** When to show floating post navigation buttons */
    postNav?: 'autohide' | 'always' | 'never';
    /** Hold duration in ms for long-press navigation gestures (400-5000) */
    longPressDuration?: number;

    // Feed preferences
    /** Show all feeds or only feeds with unread posts */
    showAllFeeds?: 'yes' | 'unread';
    /** Show welcome card when subscribing to a new feed */
    showWelcomeCard?: 'enabled' | 'disabled';
    /** Group the Home page's "New posts available" list by feed or by folder */
    homeGrouping?: 'feeds' | 'folders';
    /** View mode for Starred list view */
    starredCompactMode?: 'compact' | 'full' | 'list';
    /** Sort order for Starred list view */
    starredSortOrder?: 'oldest' | 'newest';
    /** View mode for Read Later list view */
    readLaterCompactMode?: 'compact' | 'full' | 'list';
    /** Sort order for Read Later list view */
    readLaterSortOrder?: 'oldest' | 'newest';
    /** View mode for All Items list view */
    allItemsCompactMode?: 'compact' | 'full' | 'list';
    /** Sort order for All Items list view */
    allItemsSortOrder?: 'oldest' | 'newest';

    /** Per-feed custom display names, visible only to this user. Keyed by feedId.
     * Names are 1-30 characters (trimmed). Absent or empty map means no overrides. */
    customFeedNames?: Record<string, string>;

    /** Last "mark older than X days as read" value used in the My Feeds bulk dialog (0-36500). */
    markReadOlderDays?: number;
    /** Last "but keep at least Y posts" value used in the My Feeds bulk dialog (0-100000). */
    markReadKeepCount?: number;

    /** RSS Bankruptcy state. Absent means 'none' (criteria never met yet). 'question' = the
     * criteria have been met and the user is being asked to declare (sticky until they act);
     * 'in' = user accepted and is working through their backlog; 'delay' = snoozed until
     * bankruptcyDelayUntil; 'never' = never show the bankruptcy card again. */
    bankruptcyState?: 'none' | 'question' | 'in' | 'delay' | 'never';
    /** ISO date until which the bankruptcy card stays hidden when bankruptcyState == 'delay'. */
    bankruptcyDelayUntil?: string;

    /** Whether the advanced section (unread limit sliders) of the new-feed welcome card
     * is expanded. Expanding it once keeps it expanded on future cards until collapsed. */
    welcomeCardAdvanced?: 'expanded' | 'collapsed';

    /** Dismissed one-time popups. A string of single-letter codes, one per dismissed popup.
     * Undefined/empty means nothing dismissed yet. Each dismissal appends a letter.
     * Letters: 'x' = image-only post alt/title click hint (e.g. XKCD) */
    dismissed?: string;

    /** Per-feed keyword filters, keyed by feedId. Absent or empty map means no filters.
     * Stored here so filters roam across devices; evaluation happens client-side only. */
    feedFilters?: Record<string, FeedFilter>;

    /** Per-service consent for third-party embeds (iframes and vendor embed scripts).
     * Absent key = jurisdiction default: allowed for US, blocked for EU.
     * true = user allowed the service, false = user explicitly blocked it. */
    embedConsent?: Partial<Record<EmbedService, boolean>>;

    /** Keep feeds and posts stored on this device for reading without a connection. Default false.
     * Intended for the native apps; on the web it is only offered to admins (for testing). */
    offlineReading?: boolean;
}

export type RestPreferences = Pick<RestUser, 'id'|'lastUpdate'> & { preferences: UserPreferences; };



// Feeds and Read Markers ------------------------------

/** Read marker state without feed metadata. Used in poll results when something changed here. */
export interface RestReadMarkerState {
    /** An encoded string per feed. Format: <feedId>/<sortOrder>/<viewMode>/<mode>[/<mode-data>]
     * Sort order (required): N=newest first, O=oldest first, A=newly added / not-yet-configured
     * (displays newest-first and shows the setup card). New feeds use 'A'.
     * viewMode: <showRead><compactMode>[m<maxUnread>][a<maxAgeDays>][p[<pausedUntil>]], e.g. 'uc', 'ufm50a7'.
     * The optional m/a suffixes are the per-feed auto-mark-read limits. The optional p suffix marks
     * a paused feed: 'p<semi>' paused until that semi time (ms/10000), a bare 'p' paused
     * indefinitely. While paused, clients mark every post read as it arrives.
     * mode: '0' = nothing read; 'r' = read up to index (data: index); 't' = read below index
     * (data: index); 'b' = bitfield (data: firstPost/lastPost/restRead, bitmap in bitmaps[]). */
    /* Does not need to include all feeds; only those that have read posts */
    feedMarkers: string[];
    /** Per-feed bitmaps of read posts (base64). Either an array aligned 1:1 with feedMarkers, or —
     * when most bitmaps are empty — a sparse Record keyed by feedId with the empties omitted (see
     * encodeBitmaps/decodeBitmaps in readmarker-helper). Empty bitmaps are common (only 'b'-mode
     * markers carry one), so the sparse form usually wins. */
    bitmaps: string[] | Record<string, string>;
    /** Folder structure: one entry per folder, with a list of feed IDs it contains */
    folders: Record<string, string[]>;
    /** Starred post identifiers in "feedId-postIndex" format, insertion order (newest last) */
    starred: string[];
    /** ISO 8601 timestamp of last starred change */
    starredUpdate: string;
    /** Read-later post identifiers in "feedId-postIndex" format, insertion order (newest last) */
    readLater: string[];
    /** ISO 8601 timestamp of last readLater change */
    readLaterUpdate: string;
    /** Timestamp when this record was last updated (ISO 8601 string) - the higher of ReadMarkers.lastUpdate or User.lastUpdate */
    lastUpdate: string;
}

/** Server notice, e.g. for upcoming shutdown */
export interface RestNotice {
    /** Public message to display */
    message: string;
    /** Seconds until the server shuts down, if a shutdown is planned */
    secondsTillShutdown?: number;
}

// GET /api/feeds
export interface RestFeeds extends RestReadMarkerState {
    /** List of user's subscribed feeds */
    feeds: RestUserFeed[];
    /** Server time when this response was generated (ms since epoch), captured
     *  before pending read-marker patches were flushed. Clients use it as a
     *  watermark: any patch whose RestPatchResult.serverTime <= this value is
     *  guaranteed to be reflected in this response. */
    serverTime: number;
    /** Date (YYYYMMDD) of the current home page content: fetch home/page-{homeDate}.json from the
     *  CDN. Empty when none has been published yet. */
    homeDate: string;
    /** Present when there is a notice for the user, e.g. upcoming shutdown */
    notice?: RestNotice;
    /** Current server-side preferences. Returned on every feeds fetch so the client
     *  can refresh its local copy on startup / reconnect — covers the case where the
     *  user changed a preference on another device. Locally-pending unsynced edits
     *  remain layered on top so they aren't clobbered. */
    preferences: UserPreferences;
}

// POST /api/feeds/folders
export interface RestFolderUpdate {
    /** One entry for each folder, with a list of feed IDs it contains */
    [folderName: string]: string[];
}

/** Marker for a single post index or a range of post indices (inclusive on both ends) */
export type PostMarker = number | [number, number];

/** Per-feed read marker changes */
export interface FeedReadMarkerPatch {
    /** Sort order for the feed display */
    order?: 'O' | 'N' | 'A';
    /** Show read posts: 'u' (unread only) or 'a' (all). Default is 'a' */
    showRead?: 'u' | 'a';
    /** View mode: 'c' (compact), 'f' (full) or 'l' (list). Default is 'f' */
    compactMode?: 'c' | 'f' | 'l';
    /** Auto-mark-read limit: keep at most this many newest posts unread (one of MAX_UNREAD_CHOICES).
     * 0 clears the limit. Undefined leaves it unchanged. */
    maxUnread?: number;
    /** Auto-mark-read limit: posts older than this many days are marked read (one of
     * MAX_AGE_DAYS_CHOICES). 0 clears the limit. Undefined leaves it unchanged. */
    maxAgeDays?: number;
    /** Pauses the feed until this semi time (ms/10000); PAUSED_INDEFINITELY pauses it with no end,
     * 0 unpauses it. Undefined leaves it unchanged. The server only stores it: the client marks
     * posts read while the feed is paused. */
    pausedUntil?: number;
    /** Mark all posts as read ('all') or unread ('none'). Always send postCount with it. */
    markAllRead?: 'all' | 'none';
    /** The feed's postCount as the client knew it when the user chose markAllRead. The server
     * applies markAllRead only to posts below it, so posts that arrived since (while the patch
     * was debounced, in flight, or queued offline) keep their state. Without it the server uses
     * its own current postCount (legacy clients only). */
    postCount?: number;
    /** Post indices to mark as read. Can be individual indices or [start, end] ranges (inclusive) */
    markAsRead?: PostMarker[];
    /** Post indices to mark as unread. Can be individual indices or [start, end] ranges (inclusive).
     * Takes precedence over markAllRead and markAsRead */
    markAsUnread?: PostMarker[];
}

/**
 * POST /api/feeds/markers
 * Optimized interface for updating read/unread status of many posts across many feeds
 * without uploading the entire read marker state.
 */
export interface RestReadMarkerPatch {
    /** One entry for each feed that needs to be patched, keyed by feedId */
    [feedId: string]: FeedReadMarkerPatch;
}

/**
 * POST /api/feeds/markers response.
 * serverTime is captured right after the patch was queued server-side. A
 * subsequent poll/feeds-load whose serverTime is >= this value is guaranteed to
 * already reflect this patch, so the client can stop re-layering it locally.
 */
export interface RestPatchResult {
    success: boolean;
    serverTime: number;
}



/** Star or read later changes */
export interface PostStarPatch {
    /** Removed all starred or read later posts */
    removeAll?: boolean;
    /** List of new entries to add. String format
     * is "feedId/postIndex" for single posts or "feedId/startIndex-endIndex" for ranges (inclusive)
     */
    added?: string[];
    /** List of entries to remove. Same string format as added */
    removed?: string[];
}

/** GET /api/feed/list/:id response (starred or readlater list) */
export interface RestStarList {
    /** Post references in "feedId-postIndex" format, ordered by sortOrder */
    entries: string[];
    /** Total number of entries (capped at 1000) */
    totalCount: number;
    /** ISO 8601 timestamp of last change to this list */
    lastUpdate: string;
    /** Feed metadata for feeds referenced in entries, keyed by feedId */
    feeds: Record<string, RestUserFeed>;
}


/** GET /api/feed/unread response - all unread posts across subscribed feeds */
export interface RestUnreadFeed {
    /** Post references in "feedId-postIndex" format, sorted by semiFeedPubTime */
    entries: string[];
    /** Oldest semiFeedPubTime in the returned entries */
    oldestSemi?: number;
    /** Newest semiFeedPubTime in the returned entries */
    newestSemi?: number;
    /** Server time when this response was generated (ms since epoch) */
    serverTime: number;
    /** Cursor for fetching the next page. Present when there are more entries, or when sortOrder is oldestFirst */
    cursor?: string;
    /** Feed metadata for feeds referenced in entries, keyed by feedId */
    feeds: Record<string, RestUserFeed>;
}


// Feeds ------------------------------

/**
 * This is the base interface for feed updates returned by the feed polling API.
 * It contains only the fields that can change during normal operation.
 */
export interface RestFeedUpdate {
    /** Total number of posts fetched from this feed; including those that are not archived anymore */
    postCount: number;
    /** Timestamp of the most recent new post, semi-feed-time (*) in 10-second precision (divide ms by 10000). For sorting only. */
    lastNewPostAtSemi?: number;
    /** Publication date of the most recent new post as provided by the feed (ISO 8601 string). This is shown to users. */
    lastNewPostPubDate?: string;
    /** Current status of the feed (DB statuses only) */
    feedStatus: 'BLOCK' | 'ACTIVE' | 'BAD' | 'UNUSED' | 'PRIVATE' | 'NEWSLETTER';
    /** Whether this feed's polling has been slowed down because no user has recently requested it */
    slowedDown?: boolean;

    // --- Primary location of this feed's current.dz, so the client knows where to read it from.
    // Both are absent on feeds that have never stored a post, and on feeds last stored by a server
    // that predates these fields — treat absent as "unknown" and fall back to the CDN implied by
    // the feed id. They live on the UPDATE interface, not just on RestUserFeed, because they change
    // during normal operation: a feed's very first store is what gives it a location at all, and a
    // feed that crosses the CDN subscriber threshold (or moves instance) changes it later. A client
    // that only ever learned the location at subscribe time would keep reading from the CDN for a
    // feed whose current.dz was never uploaded there. ---
    /** CDN serving this feed's current.dz. `null` means it is not on a CDN and must be fetched
     * from the sync instance below; the feed's archive files (pN.dz) are on the CDN regardless. */
    cdn?: FeedCdn | null;
    /** Sync-server instance holding this feed's current.dz (e.g. '01', 'e1'). */
    syncInstance?: string;
    /** Content hash of the per-site stylesheet for this feed's site (sync-server/site-css), or
     * null/absent when the site has none. The client loads `site-css/<domain>.<hash>.css` from
     * the CDN and tags the feed's post cards with `feed-<domain>`. On the UPDATE interface because
     * a changed stylesheet gets a new hash on the feed's next fetch. */
    siteCss?: string | null;
}

// GET /api/feed/:id
export interface RestUserFeed extends RestFeedUpdate {
    /** Object type discriminator */
    objectType: 'feed' | 'catalog';
    /** Unique identifier for the feed */
    id: string;
    /** Display title of the feed */
    title: string;
    /** Short version of the title for display in compact views */
    shortTitle?: string;
    /** Human-readable URL-friendly identifier (e.g. "nytimes", "techcrunch") */
    friendlyId?: string;
    /** URL of the RSS/Atom feed */
    url: string;
    /** URL of the site */
    siteUrl: string;
    /** Render the icon as a circle (e.g. YouTube channel avatars) rather than a square. */
    circularIcon?: boolean;

    // The current.dz location (cdn / syncInstance) is inherited from RestFeedUpdate above.

    /** Semi-feed-times (ms/10000, newest first) of recent posts, for client-side auto-mark-read.
     * recentPostsSemi[i] belongs to post index (postCount - 1 - i) at response time, trimmed to the
     * relevant age window plus one older
     * sentinel entry. Present in GET /api/feeds only for feeds with an AGE limit (maxAgeDays) — the
     * only limit that needs post times — each trimmed to its own window, and for feeds whose pause
     * has ended (trimmed to the posts since the pause end), so the client can mark the posts that
     * arrived during the pause read; and in GET
     * /api/feed/:id?postTimes=1 (trimmed to the largest selectable age, for the limits-preview UI). */
    recentPostsSemi?: number[];

    // --- CDN-served feed images. When present, the feed has that image stored in its CDN folder,
    // which the client derives from the feed id: `<cdnUrl>/<feedId>` (cdnUrl is the
    // jurisdiction-aware base from the auth server). Vector ('svg') is served as
    // `<cdnUrl>/<feedId>/<kind>.svg`; bitmaps as `<cdnUrl>/<feedId>/<kind>-w{32,48,96,192}.webp`
    // (kind = 'favicon' | 'feedimage'). Admins can also fetch the bytes via
    // GET /api/admin/feed/:id/image. ---
    /** Original format of the favicon image, if the feed has one. */
    faviconExt?: FeedImageExt;
    /** Original format of the RSS feed image, if the feed has one. */
    feedImageExt?: FeedImageExt;
    /** Format of the admin-uploaded custom icon, if one has been uploaded (vector svg or bitmap webp). */
    customImageExt?: 'svg' | 'webp';
    /** Which image is the preferred default for this feed. */
    imageDefault?: ImageDefault;

    /** Public comment visible to all users */
    downreadPublicComment?: string;
    /** Average posting frequency in posts per day (calculated from recent posts) */
    postFrequency?: number;
    /** Approximate per-post averages of this feed's content: words, images, videos, audio (see
     * PostStats). Absent until the sync server has analyzed a fetch of the feed. For feed-info
     * display only: deliberately NOT part of RestFeedUpdate, so polling never carries it. */
    postStats?: PostStats;
    /** Copyright notice from the RSS feed */
    copyright?: string;
    /** Public feed responses (/api/public/feed*) only: what the RSS Directory may show of the posts,
     * from the feed's catalog entry (also 'hidden' for a hidden, inactive entry). Unset = 'excerpt'. */
    previewMode?: PreviewMode;
    /** Description/tagline of the feed from the RSS feed */
    description?: string;
    /** Feed category determined by AI analysis */
    category?: string;
    /** Whether the feed is mostly a paid subscription service. Null if unknown. */
    paid: boolean | null;
    /** Feed type determined by AI analysis */
    type: RssType;
    /** Keywords relevant to the feed content (up to 5) */
    keywords?: string[];
    /** Current fetch interval in minutes (informational only) */
    fetchIntervalMins?: number;
    /** Whether the feed has an active WebSub (push notification) subscription */
    webSubActive?: boolean;
    /** True when WebSub is active but the hub has been missing notifications (polling hourly as fallback) */
    webSubBroken?: boolean;
    /** Type of error if feed is in error state: HTTP (connection/HTTP error), NO_RSS (not a feed), PARSE (feed parsing error) */
    errorType?: 'HTTP' | 'NO_RSS' | 'PARSE' | 'BLOCKED' | null;
    /** Base64-encoded AES-256-GCM key for decrypting this private feed's DRP2e post files.
     * Only present for PRIVATE feeds the user is subscribed to. */
    passphrase?: string;
    /** Source format of the feed as reported by the parser. 'json' is reserved for future support. */
    sourceFormat?: 'rss' | 'atom' | 'json';

    /** Count of consecutive fetch errors. Surfaced to users so the UI can flag broken feeds. */
    fetchErrorCount?: number;
    /** Server-time when the current run of consecutive fetch failures began, cleared on the
     * next success (ISO 8601 string). Together with fetchErrorCount it drives the "feed has a
     * problem" flag in the UI. */
    fetchFailingSinceAt?: string;
    /** Timestamp of the last successful fetch, server-time (ISO 8601 string). Lets previews
     * warn that the shown content may be stale for rarely-polled feeds. */
    lastFetchedAt?: string;
    /** True when the feed currently only works through the residential proxy (the origin blocks
     * everything else, e.g. bot protection). Such feeds update at most once a day. */
    residentialProxy?: boolean;
}

// Semi-feed-time explanation;
// * = semi-feed-time: The timestamp is based on the feed's time, but adjusted
//     if it does not deviate more than one hour from the server time.
//     If it is a new feed, we trust the feed time if it is in the past. Otherwise
//     we set it to the current server time.
//     Semi-feed-time uses 10-second precision: divide milliseconds by 10000 to fit in 32 bits.
//     This gives us a range of ~1360 years from epoch (until year 3330).
//     
//     IMPORTANT: Each post's semiFeedPubTime is guaranteed to be at least 1 higher than the
//     preceding post. This ensures posts are always sorted in the correct order, even when
//     multiple posts have the same or very close publication times.
//
// Semi-feed time is used for sorting, but never displayed.
//

// GET /api/admin/feed/:id
export interface RestAdminFeed extends RestUserFeed {
    /** Number of users who currently subscribe to this feed */
    subscriberCount: number;

    /** Optional tag for marking test data */
    tag?: string;

    /** The post number of the post that has first been archived */
    firstArchivedPostIndex: number;
    /** Whether the feed has been manually verified by a user */
    manuallyVerified: boolean;
    /** Timestamp when the feed was last manually verified (ISO 8601 string) */
    lastVerifiedAt?: string;
    /** User ID of who verified the feed */
    verifiedByUserId?: string;
    /** Count of consecutive fetch errors */
    fetchErrorCount: number;

    /** Timestamp when the feed was added, server-time (ISO 8601 string) */
    createdAt: string;
    /** Timestamp of the last configuration change, server-time (ISO 8601 string) */
    lastConfigChangeAt: string;

    /** Scheduled timestamp for the next fetch (ISO 8601 string) */
    nextFetchAt?: string;

    /** Private comment visible only to admins */
    downreadPrivateComment?: string;

    /** Whether the feed has an active catalog entry */
    hasCatalog: boolean;

    /** Declared feed language (e.g. 'en', 'de'), as detected/stored on the feed. */
    language?: string;

    /** All URL aliases registered for this feed (authoritative for URL resolution) */
    aliases?: string[];

    /** Candidate aliases awaiting manual verification — NOT yet authoritative for lookups.
     * Admins should review these (esp. host mismatches vs siteUrl) before verifying. */
    pendingAliases?: string[];

    /** Aliases that could not be promoted on the last verification because they already
     * map to a different feed. Surfaced so admins are aware of the conflict. */
    aliasConflicts?: { aliasUrl: string; ownedByFeedId: string }[];

    /** WebSub hub URL (if subscribed) */
    webSubHubUrl?: string;
    /** WebSub subscription renewal date (ISO 8601) */
    webSubNextRenew?: string;
    /** Timestamp of last WebSub subscription verification from hub (ISO 8601) */
    webSubLastVerify?: string;
    /** Timestamp of last WebSub content notification from hub (ISO 8601) */
    webSubLastNotify?: string;
    /** Count of consecutive WebSub subscription failures */
    webSubFailures?: number;
    /** ISO 8601 timestamp until which broken WebSub mode is active */
    webSubBrokenUntil?: string;
    /** WebSub: when the last 'subscribe' request was sent to the hub (ISO 8601) */
    webSubSubscribedAt?: string;
    /** WebSub: when the next subscription attempt is due after failures (ISO 8601) */
    webSubRetryAt?: string;
    /** User ID who created this feed (for READY/PRIVATE feeds) */
    creatorId?: string;

    /** Detail of the most recent fetch failure — the reader's error message plus the transport and
     * conditional validators used for that request. Absent once the feed fetches successfully again. */
    lastFetchError?: string;

    /** Name of the proxy currently used to fetch this feed (e.g. "cf-prod"). Null/absent means direct fetching. */
    proxyName?: string | null;
    /** ISO 8601 timestamp when the current proxy was first used. Cleared after ~90 days so direct fetching is re-tried. */
    proxyFirstUsedAt?: string | null;
    /** ISO 8601 timestamp of the last fetch attempt through the residential proxy (at most one per 24h). */
    residentialProxyAt?: string | null;

    /** Maximum number of posts we are willing to store for this feed (>= MIN_STORED_POSTS). */
    maxStoredPosts?: number;

    /** One-sentence, target-audience-facing description generated by the AI feed analysis on the
     * initial fetch. Used as the default catalog description in the admin editors. */
    aiDescription?: string;
}

// GET /api/admin/feed/:id/image?kind=favicon|feedimage&res=original|w32|w48|w96|w128|w192|w256&format=<ext>
// Admin-only: returns the stored image as raw binary with its Content-Type header, served from the
// sync server's local archive. Regular users fetch images straight from the CDN instead
// (see RestUserFeed.faviconExt/feedImageExt).

// POST /api/agent/feed/:id
export interface RestAgentFeedUpdate {
    feedStatus?: RestAdminFeed['feedStatus'];
}

// POST /api/agent/user/:id
export interface RestAgentUserUpdate {
    userStatus: 'active' | 'suspended' | 'deleted';
}

// POST /api/admin/feed/:id
export interface RestAdminFeedUpdate {
    /**
     * Whether the feed has been manually verified by a user.
     * Setting this to true updates lastVerifiedAt to current time and verifiedByUserId to the current admin's ID.
     * Setting false clears lastVerifiedAt and verifiedByUserId. Omit to leave unchanged.
     */
    manuallyVerified?: boolean;
    /**
     * Manually override the feed status. Will clear errorType if setting to ACTIVE or UNUSED. lastConfigChangeAt will be updated.
     */
    feedStatus?: RestAdminFeed['feedStatus'];

    /** Private comment visible only to admins. Also updates lastConfigChangeAt. */
    downreadPrivateComment?: string;

    /** Public comment visible to all users. Also updates lastConfigChangeAt. */
    downreadPublicComment?: string;

    /**
     * Update the RSS feed URL. This will reset the feed status to NEW and trigger reprocessing.
     * lastConfigChangeAt will be updated.
     */
    url?: string;

    /** Update the site URL. lastConfigChangeAt will be updated. */
    siteUrl?: string;

    /** Short title displayed in compact views (e.g. left sidebar). Trimmed; empty string clears it. */
    shortTitle?: string;

    /** Feed category (must be in FeedCategoryList). Empty string clears it. Also updates lastConfigChangeAt. */
    category?: string;

    /** Maximum number of posts we are willing to store for this feed. Must be >= MIN_STORED_POSTS
     * (1200). Also updates lastConfigChangeAt. */
    maxStoredPosts?: number;

    /** Override which image is the feed's default icon. Also updates lastConfigChangeAt. */
    imageDefault?: ImageDefault;

    /** Format of the uploaded custom icon (vector svg or bitmap webp). Also updates lastConfigChangeAt. */
    customImageExt?: 'svg' | 'webp';
}

// POST /api/feed/subscribe
export interface RestSubscribe {
    /** Feed ID to subscribe to */
    feedId: string;
}

/** Response from POST /api/feed/subscribe */
export interface RestSubscribeResult {
    feed: RestUserFeed;
    /** Encoded marker string for the initial read state (same format as RestReadMarkerState.feedMarkers entries) */
    feedMarker: string;
    /** Base64-encoded bitmap for read posts (same format as RestReadMarkerState.bitmaps entries) */
    bitmap: string;
    /** True when the subscription actually started a fetch of the feed (absent/false when the feed
     *  was fetched recently enough, or for newsletter feeds). Lets the client show a
     *  "refreshing" indicator for the feed. */
    refreshing?: boolean;
}

// POST /api/feed/url - Get feed by URL alias
export interface RestRequestFeedByUrl {
    /** URL of the RSS/Atom feed, or URL of an HTML page with a feed link tag */
    url: string;
}

/** Cached feed metadata from sync-server /api/fetch — not a DB entity */
export interface CachedFeedResult {
    /** Actual URL after redirects */
    url: string;
    /** The URL the user originally requested */
    originalUrl: string;
    title: string;
    siteUrl: string;
    description?: string;
    /** Copyright notice from the RSS feed */
    copyright?: string;
    category?: FeedCategory;
    type: RssType;
    keywords?: string[];
    shortTitle?: string;
    paid: boolean | null;
    /** Render the icon as a circle (e.g. YouTube channel avatars) rather than a square. */
    circularIcon?: boolean;
    language?: string;
    friendlyId?: string;
    /** Fetch error info (mutually exclusive with success fields) */
    error?: { type: 'HTTP' | 'NO_RSS' | 'PARSE' | 'BLOCKED'; message?: string };
    /** Precheck failure reason */
    precheckFailure?: 'no-rss' | 'illegal' | 'adult' | 'bad-url';
    /** Recent posts from the just-fetched feed (most-recent-first). Used by the api server to detect
     *  when this URL is an alternate URL of an already-tracked feed. Capped at 50 items. */
    lastPosts?: PostMeta[];
}

// POST /api/feed/request - Request a feed (creates if doesn't exist)
// Returns RestUserFeed if feed is ACTIVE, RestFeedStatus otherwise
export interface RestFeedStatus {
    /** FETCHING = sync-server is still fetching; READY = result available with cachedFeed; BAD/BLOCK = error */
    feedStatus: 'FETCHING' | 'READY' | 'BAD' | 'BLOCK' | RestUserFeed['feedStatus'];
    /** Type of error if feed is in error state */
    errorType?: 'HTTP' | 'NO_RSS' | 'PARSE' | 'BLOCKED' | null;
    /** Feed ID — present when returning an existing DB feed */
    feedId?: string;
    /** Cached feed metadata — present when feedStatus is READY (from sync-server cache) */
    cachedFeed?: CachedFeedResult;
}

// POST /api/feed/subscribe-with-mode - Subscribe based on cached feed data
export interface RestSubscribeWithMode {
    /** Feed URL (must be available in sync-server cache) */
    url: string;
    /** 'public' = create ACTIVE feed; 'private' = create PRIVATE feed */
    mode: 'public' | 'private';
}

// POST /api/newsletter - Create a newsletter (inbound-email) feed
export interface RestCreateNewsletter {
    /** User-entered title for the newsletter feed */
    title: string;
}

/** Response from POST /api/newsletter */
export interface RestCreateNewsletterResult extends RestSubscribeResult {
    /** The inbound email address that feeds this newsletter */
    email: string;
}

// Unsubscribe:
// DELETE /api/feed/:id


// Feed polling
// POST /api/feed/poll
export interface RestFeedPollRequest {
    // this is last call's server time (ms since epoch). 0 for first poll
    lastServerTime: number;
}

export interface RestFeedPollResult {
    // this is the server time (ms since epoch) when the poll was done
    serverTime: number;

    // the next poll should be done at this time (ms since epoch. Use relative to serverTime!)
    nextPollSuggestedTime: number;

    // All updates since last poll, by id
    updates: Record<string, RestFeedUpdate>;

    /** Present if markers changed since lastServerTime */
    markers?: RestReadMarkerState;

    /** Present when there is a notice for the user, e.g. upcoming shutdown */
    notice?: RestNotice;
}

// Anonymous (no-login) endpoints ------------------------------
// The client holds its subscription list, read markers, preferences, starred and read-later in
// localStorage; these endpoints are stateless and driven entirely by the supplied feedIds.

/** POST /api/anon/feeds — body. Up to 30 public feed ids the anonymous client follows. `seq` is the
 *  anon engine's per-session call counter (0 on the first call, incremented after each); the server
 *  uses seq==0 to count distinct anonymous sessions ("logins"). */
export interface RestAnonFeedsRequest {
    feedIds: string[];
    seq?: number;
}

/** POST /api/anon/feeds — response. Just the feed metadata (no markers/preferences/folders, all
 *  client-side). Each feed carries `recentPostsSemi` capped at ANON_SEMI_CAP so the client can build
 *  its unread list locally. */
export interface RestAnonFeeds {
    feeds: RestUserFeed[];
    serverTime: number;
    notice?: RestNotice;
}

/** POST /api/anon/poll — body. */
export interface RestAnonPollRequest {
    feedIds: string[];
    /** Last call's server time (ms since epoch); 0 for the first poll. */
    lastServerTime: number;
}

/** A poll delta for a changed feed, plus its newest post times so the client can time-sort the
 *  newly-arrived posts in its unread list. */
export interface RestAnonFeedUpdate extends RestFeedUpdate {
    /** Newest post times (semi, newest-first, ≤ ANON_SEMI_CAP) for this changed feed. */
    recentPostsSemi?: number[];
}

/** POST /api/anon/poll — response. Like the authenticated poll minus all read-marker state. */
export interface RestAnonPollResult {
    serverTime: number;
    nextPollSuggestedTime: number;
    updates: Record<string, RestAnonFeedUpdate>;
    notice?: RestNotice;
}

// Catalog ------------------------------

// GET /api/catalog/:feedId
export interface RestCatalog extends RestUserFeed {
    /** Object type discriminator */
    objectType: 'catalog';
    /** Promotion priority - higher values are more visible */
    promotion: number;
    /** Cached subscriber count (synced periodically for stable sorting) */
    catalogSubscriberCount: number;
    /** When this entry was added to the catalog (ISO 8601 string) */
    dateAdded: string;
    /** Keywords extracted from the feed */
    keywords?: string[];
    /** Description of the feed for catalog display */
    catalogDescription: string;
    /** Domain name without TLD for text search (e.g., "nytimes") */
    domain: string;
    /** Render the icon as a circle (e.g. YouTube channel avatars) rather than a square. */
    circularIcon?: boolean;
    /** Header image name from ImageCollection */
    headerImage?: string;
    /** Member feed IDs grouped under this catalog entry (parent only) */
    children?: string[];
    /** Feed ID of the parent catalog this feed belongs to (children only) */
    parent?: string;
    /** Admin override of the feed's text badge (see utils/post-stats-badges). Absent = detected
     * from postStats. */
    textType?: PostTextType;
}

// GET /api/catalog/:feedId (admin view)
export interface RestCatalogAdmin extends RestCatalog {
    /** User ID of who edited this catalog entry */
    editorId: string;
    /** Date of last edit (ISO 8601 string) */
    lastEdited: string;
}

// GET /api/catalog/categories
export interface RestCatalogCategories {
    /** List of unique categories in the catalog */
    categories: string[];
}

// GET /api/catalog/autocomplete?q=term
// [feedId, title, domain, feed URL]; the URL (for links to the directory's /feed/view?url=) is missing
// only while the API runs with an older sync-server.
export type RestCatalogAutocomplete = Array<[string, string, string, string?]>;

/** A result of GET /api/catalog/directory-search: a catalog search for the directory site
 *  yellowfeeds.com, with only what its feed listings show (ready to render). */
export interface RestDirectoryFeed {
    id: string;
    friendlyId?: string;
    /** Display title (the short title if the feed has one). */
    title: string;
    /** RSS/Atom URL */
    url: string;
    siteUrl?: string;
    /** Feed icon (192px), or the generated default icon. */
    iconUrl: string;
    circularIcon?: boolean;
    /** The feed's own description as plain text (the listing's tagline), at most 200 characters. */
    description?: string;
    catalogDescription?: string;
    category?: string;
    /** Content badges ('full posts', 'videos', ...), as Downread's feed tile shows them. */
    badges: string[];
    /** Posts per day; only for catalog feeds listed for 48 hours, like on the feed tile. */
    postFrequency?: number;
    paid?: boolean;
    lastNewPostPubDate?: string;
    /** What the feed page may show of the posts; unset = 'excerpt'. */
    previewMode?: PreviewMode;
    /** Copyright notice from the feed, shown with the feed page's post preview. */
    copyright?: string;
    // What the directory's feed page needs to show the latest posts without loading the feed
    // again (it caches the search results): see the same fields of RestUserFeed.
    postCount: number;
    cdn?: FeedCdn | null;
    syncInstance?: string;
    siteCss?: string | null;
}

// GET /api/catalog/directory-search?q=
export interface RestDirectorySearch {
    /** Best match first, at most DIRECTORY_SEARCH_LIMIT. */
    items: RestDirectoryFeed[];
    /** There are more matches than items. */
    hasMore: boolean;
}

export const DIRECTORY_SEARCH_LIMIT = 100;

// POST /api/admin/catalog
export interface RestCatalogCreate {
    /** Feed ID */
    feedId: string;
    /** Overrides title shown to user */
    title: string;
    /** Overrides description shown to the user */
    description: string;
    /** Description of the feed for catalog display */
    catalogDescription: string;
    /** Whether this catalog entry is active (default: true) */
    active?: boolean;
    /** Header image name from ImageCollection */
    headerImage?: string;
    /** Promotion priority (default: 100) */
    promotion?: number;
    /** Keywords extracted from the feed */
    keywords?: string[];
    /** Category */
    category?: string;
    /** Type */
    type?: RssType;
    /** Whether the feed is mostly a paid subscription service */
    paid: boolean | null;
    /** Domain name for text search (e.g., "nytimes.com") */
    domain: string;
    /** Member feed IDs to group under this catalog entry. Setting this auto-updates the children's `parent` field; removing children clears their `parent`. */
    children?: string[];
    /** Admin override of the feed's text badge. Undefined leaves it unchanged (update) or unset
     * (create); null clears it, so the badge is detected from postStats again. */
    textType?: PostTextType | null;
    /** What the RSS Directory may show of the posts; undefined leaves it unchanged (update) or
     * unset = 'excerpt' (create). Set by publishers through the partner API; 'hidden' there also
     * deactivates the entry. */
    previewMode?: PreviewMode;
}

/** Lightweight feed summary used in batch lookups (FeedTile children/parent display) */
export interface RestFeedSummary {
    id: string;
    /** Catalog title preferred; falls back to feed.shortTitle / feed.title only if no catalog exists */
    shortName: string;
    /** Catalog domain (when feed has a catalog entry) — used to disambiguate when several feeds share the same title */
    domain?: string;
    /** User-facing CDN URL of the feed's selected icon (new image system), already resolved server-side. */
    iconUrl?: string;
    /** Render the icon as a circle (e.g. YouTube channel avatars) rather than a square. */
    circularIcon?: boolean;
}

// POST /api/feeds/summaries
export interface RestFeedSummariesRequest {
    /** Feed IDs to look up. Max 30. */
    feedIds: string[];
}

// PUT /api/admin/catalog/:feedId
export type RestCatalogUpdate = Partial<Omit<RestCatalogCreate, 'feedId'>>;

// Admin Operations ------------------------------

// POST /api/admin/delete-tag
export interface RestAdminDelete {
    tag: string;
    magic: string;
}

// POST /api/admin/feed-description
export interface RestFeedDescriptionRequest {
    feedId: string;
    extraInstructions?: string;
}

// POST /api/admin/spellcheck
export interface RestSpellcheckRequest {
    text: string;
    extraInstructions?: string;
}

// POST /api/admin/description
export interface RestDescriptionRequest {
    description: string;
    extraInstructions?: string;
}

// POST /api/admin/generate-image
export type ImageGenerationModel = 'google/gemini-2.5-flash-image' | 'google/gemini-3.1-flash-image-preview' | 'google/gemini-3-pro-image-preview' | 'openai/gpt-5.4-image-2';
export const DEFAULT_IMAGE_MODEL: ImageGenerationModel = 'google/gemini-2.5-flash-image';

export interface RestGenerateImage {
    /** The prompt to send to the image generation engine */
    prompt: string;
    /** The name to store the generated image under (same validation as upload) */
    name: string;
    /** Optional name of input image on Downread CDN (for image-to-image generation) */
    inputImage?: string;
    /** Optional base64-encoded image pasted by the user as reference */
    pastedImageBase64?: string;
    /** Image generation model to use (defaults to gemini-2.5) */
    model?: ImageGenerationModel;
}

// POST /api/admin/feed-icon/upscale
export interface RestUpscaleFeedIcon {
    /** Feed ID whose icon should be upscaled */
    feedId: string;
    /** Source image URL: either an external http(s) URL to download, or a data: URL */
    url: string;
    /** Image generation model to use (defaults to gemini-2.5) */
    model?: ImageGenerationModel;
}

export interface RestUpscaleFeedIconResponse {
    /** CDN URL where the upscaled 96x96 logo.png was uploaded */
    url: string;
}

// POST /api/admin/feed/:id/custom-image/upscale
// AI-upscales a source image (favicon or feed image) into the feed's custom image (192px max).
// Returns the updated RestAdminFeed. The upload variant (multipart) has no JSON body.
export interface RestUpscaleCustomImage {
    /** Source image URL to upscale: an http(s) URL or a data: URL. */
    url: string;
    /** Image generation model to use (defaults to gemini-2.5). */
    model?: ImageGenerationModel;
}

/** How a non-square raster custom image is made square: 'pad' = fit it whole and fill the rest
 * with white (default), 'crop' = fill the square and cut off the overflowing edges (centered).
 * SVGs are stored as-is either way. */
export const SQUARE_FITS = ['pad', 'crop'] as const;
export type SquareFit = typeof SQUARE_FITS[number];

// POST /api/admin/feed/:id/custom-image/url
// Downloads an image (PNG, JPG, WEBP, GIF, AVIF or SVG; max 2 MB) from a public http(s) URL — e.g. a
// logo found on the feed's site — and stores it as the feed's custom image, like an upload. Returns
// the updated RestAdminFeed.
// (The multipart upload, POST /api/admin/feed/:id/custom-image/upload, takes the fit as ?fit=.)
export interface RestCustomImageFromUrl {
    /** Public http(s) URL of the image (max 2000 chars). */
    url: string;
    /** Default 'pad'. */
    fit?: SquareFit;
}

// PUT&POST /api/admin/feed/alias
export interface RestFeedAliasResponse {
    /** The feed ID */
    feedId: string;
    /** The URL being checked */
    url: string;
    /** Whether this URL is an alias for this feed */
    isAlias: boolean;
    /** Set when a (non-force) set request was refused because the alias already maps to a
     * different feed. The alias was NOT written; re-send with force:true to overwrite. */
    conflict?: { aliasUrl: string; ownedByFeedId: string };
}

// GET /api/admin/feed/:id/duplicates
export interface RestAdminFeedDuplicate {
    id: string;
    title: string;
    shortTitle?: string;
    url: string;
    siteUrl: string;
    feedStatus: RestAdminFeed['feedStatus'];
    subscriberCount: number;
    manuallyVerified: boolean;
    createdAt: string;
    lastNewPostPubDate?: string;
}

export interface RestAdminFeedDuplicates {
    /** Other feeds sharing this feed's siteUrl (or having it as an alias) */
    feeds: RestAdminFeedDuplicate[];
    /** Up to 10 other public feeds whose siteUrl is on the same domain (host without www.),
     * excluding those already in `feeds` */
    sameDomain: RestAdminFeedDuplicate[];
}

// GET /api/admin/api-servers — one configured API server instance the admin can target.
export interface RestApiServer {
    /** Instance id (e.g. '01') — matches FeedReport.instance and the user-id prefix. */
    instance: string;
    /** Hostname of the instance, e.g. "api01.downread.com". */
    name: string;
    /** Base URL the admin client can send requests to, e.g. "https://api01.downread.com". */
    url: string;
}

// GET /api/admin/feed/:id/creator — who created a feed, as seen by the queried API server.
// Public feeds no longer store creatorId; their creator is tracked in a user's createdFeeds, which
// lives only on that user's own API instance — so a 'none' result means "not on this server, try
// another". Private/NEWSLETTER feeds still carry creatorId on the feed row and resolve anywhere.
export interface RestFeedCreatorResult {
    feedId: string;
    /** Resolved creator user id, if known on this server; undefined when not found here. */
    creatorUserId?: string;
    /** 'feed' = from feed.creatorId (private/newsletter); 'createdFeeds' = a user on this server
     *  lists the feed as created; 'none' = not found on this server. */
    source: 'feed' | 'createdFeeds' | 'none';
    /** The full creator user — only present when that user's record lives on THIS server. */
    user?: RestUser;
}

// GET /api/admin/users/recent — a lightweight row for the "recently created users" admin list.
// Sourced from THIS instance's users table (createdAt lives on the API user, not the auth account).
// Admin flag + last login come separately from the auth service (RestAccountSummary), keyed by
// accountId, so this stays a cheap single-table read.
export interface RestRecentUser {
    id: string;
    accountId: string;
    /** Optional tag marking test/batch data. */
    tag?: string;
    userStatus: 'active' | 'suspended' | 'deleted';
    userPlan: 'free' | 'pro' | 'vip' | 'earlyAdopter' | 'test';
    /** ISO 8601 creation timestamp; the list is ordered newest-first by this. */
    createdAt: string;
}

// PUT&POST /api/admin/feed/alias
export interface RestFeedAliasRequest {
    /** The feed ID to check or add an alias for */
    feedId: string;
    /** The URL to check or use as an alias */
    url: string;
    /** Overwrite consent: when true, an alias already owned by a different feed will be
     * reassigned to this feed. When false/absent, such a request is refused with a conflict. */
    force?: boolean;
}

// OPML import/export ------------------------------

// POST /api/opml/parse
export interface RestOpmlImportRequest {
    /** Raw OPML XML as text */
    xml: string;
}

/** One feed entry inside a parsed OPML file */
export interface RestOpmlFeedEntry {
    url: string;
    title?: string;
    /** Top-level folder name, if the feed was nested under one. Deeper hierarchies are flattened. */
    folder?: string;
    /** True when the OPML carries a downreadPrivate="true" attribute (set by Downread's own export) */
    preferPrivate?: boolean;
}

export interface RestOpmlImportResult {
    feeds: RestOpmlFeedEntry[];
    /** Top-level folder names found in the OPML, in declaration order */
    folders: string[];
}

// User data export (GDPR Art. 20) ------------------------------

/** One subscribed feed in a data export, with its folder resolved to a name. */
export interface RestExportSubscription {
    feedId: string;
    title: string;
    url: string;
    siteUrl: string;
    feedStatus: 'BLOCK' | 'ACTIVE' | 'BAD' | 'UNUSED' | 'PRIVATE' | 'NEWSLETTER';
    /** Resolved folder name; null when the feed is in the unfiled bucket. */
    folder: string | null;
    /**
     * Highest post index this feed has at export time (= postCount - 1).
     * Combined with `lastPublishedAt`, an importer can align their own database's
     * post indexes for this feed with the references in starred/readLater/feedMarkers.
     * Omitted when the feed has no posts yet.
     */
    highestPostIndex?: number;
    /** Publication date of the highest-indexed post, as ISO 8601 (from the feed itself, not server time). Omitted when unknown. */
    lastPublishedAt?: string;
}

/**
 * The Downread API's contribution to the user's full data export. The frontend
 * wraps this with auth-server account info and a plaintext email read from the
 * browser's localStorage to produce the final downloaded file.
 *
 * GET /api/user/export — see userDataExportHandler.
 */
export interface RestUserDataExport {
    profile: {
        userId: string;
        nickName: string;
        avatarUrl?: string;
        userStatus: 'active' | 'suspended' | 'deleted';
        userPlan: 'free' | 'pro' | 'vip' | 'earlyAdopter' | 'test';
        userPaidUntil?: string;
        createdAt: string;
        preferences: UserPreferences;
    };
    subscriptions: RestExportSubscription[];
    readState: {
        feedMarkers: string[];
        bitmaps: string[];
        starred: string[];
        starredUpdate: string;
        readLater: string[];
        readLaterUpdate: string;
    };
}

/// Stuff interfaces ------------------------------
// ⚠️ Adding a stuff payload interface here means a new stuff key exists.
// Document it in downread/STUFF.md (the inventory of every stuff key).

// stuff 'purge/archive-cleanup' (admin-only; written by the sync-server archive-purge job)
export interface ArchivePurgeStuff {
    /** Last completed purge session, in Eastern day-of-month + hour (one of 1/3/5). */
    lastPurge?: { day: number; hour: number };
    /** Cursor: id of the last feed processed. Absent = start from the beginning next session. */
    lastFeedId?: string;
    /** Most recent purge sessions, newest first (capped). */
    history?: {
        /** ISO timestamp when the session finished. */
        at: string;
        feedsInspected: number;
        /** Archives deleted (each archive's 3 physical copies count as one). */
        archivesDeleted: number;
        /** Id of the last feed processed in the session, or null if none. */
        stoppedAtFeedId: string | null;
    }[];
}

// stuff 'discover/config'
export interface DiscoverConfig {
    collections: DiscoverCollection[];
}

export interface DiscoverCollectionFeed {
    iconUrl: string;
    /** Render the icon as a circle (e.g. YouTube channel avatars) rather than a square. */
    circularIcon?: boolean;
    feedId: string;
    title: string;
    /** Feed's site URL, populated by the discover content generator from the feed's (NOT NULL) DB column. */
    siteUrl: string;
    /** Set by the daily feed-activity job (sync-server/jobs/feed-activity-job.ts) to the day
     *  (YYYY-MM-DD) on which this feed was first detected inactive (no post in 30 days).
     *  Absent/empty means active. Inactive feeds are excluded from generated discover/onboarding
     *  content and surfaced on the admin "Inactive Feeds" page. Reactivated feeds get it cleared. */
    inactiveSince?: string;
    /** Preferred for the collection's random selection on the Home page (the rest only fills up
     *  when there aren't enough promoted feeds). Never affects the order. */
    promoted?: boolean;
}

export interface DiscoverCollection {
    id: string;
    title: string;
    feedIds: DiscoverCollectionFeed[];
    /** User-facing topic name of the collection (the admin-only `title` is used when unset). */
    topic?: string;
    /** Sort order: higher numbers are shown first. Unset sorts last. */
    priority?: number;
}

// stuff 'discover/onboarding' (also published on the CDN as discover/onboarding.json)
export interface DiscoverOnboardingCollection {
    id: string;
    topic: string;
    priority?: number;
    /** All (active) feeds in the collection. */
    feeds: DiscoverCollectionFeed[];
}

/** Global Downread vitality stats, refreshed hourly by the onboarding job. */
export interface DiscoverStats {
    /** Total number of feeds in Downread (DB COUNT over feeds). */
    feeds: number;
    /** Total number of posts across all feeds (DB SUM of postCount). */
    posts: number;
    /** New posts added since the previous hourly run (>= 0). */
    postsLastHour: number;
}

export interface DiscoverOnboarding {
    collections: DiscoverOnboardingCollection[];
    /** Global Downread stats, refreshed hourly. Absent on older cached content. */
    stats?: DiscoverStats;
    /** Most recently added public, manually-verified feeds (newest first, max 18).
     *  Absent on older cached content. */
    latestFeeds?: DiscoverCollectionFeed[];
    /** 20 randomly chosen catalog feeds, new every hour (Add Feed's "Random" collection).
     *  Absent on older cached content. */
    randomFeeds?: DiscoverCollectionFeed[];
    /** 20 feeds picked at random every hour from the 100 catalog feeds with the most posts per day
     *  (Add Feed's "High frequency" collection), busiest first. Absent on older cached content. */
    highFrequencyFeeds?: DiscoverCollectionFeed[];
    /** Timestamp when this content was last generated (ISO 8601 string) */
    lastUpdated: string;
}

// stuff 'home/page' (also published on the CDN as home/page-{YYYYMMDD}.json; clients learn the date
// from RestFeeds.homeDate). Regenerated hourly; categories and feeds change once a day.
export interface HomePageContent {
    /** The highest-priority collections (see HOME_CATEGORY_COUNT), highest priority first. */
    categories: HomeCategory[];
    /** Global Downread stats, refreshed hourly. */
    stats: HomeStats;
    /** Timestamp when this content was last generated (ISO 8601 string) */
    lastUpdated: string;
    /** Date in YYYYMMDD format (Eastern timezone) */
    date: string;
}

export interface HomeCategory {
    /** The collection id. */
    id: string;
    /** URL-friendly id: the title lower-cased, letters and digits only (unique within the page).
     *  Used for /a/home/{slug} and the category file home/category/{slug}-{YYYYMMDD}.json. */
    slug: string;
    /** The collection's topic (its admin title when no topic is set). */
    title: string;
    priority?: number;
    /** In home/page: a random selection of the collection's feeds (see HOME_FEEDS_PER_CATEGORY), in
     *  random order. In home/category/{slug}-{YYYYMMDD}.json: all of them, in the same order. */
    feeds: HomeFeed[];
}

export interface HomeFeed {
    id: string;
    title: string;
    /** CDN URL of the feed's icon (128px, the tile size; 192px until the feed's 128px variant exists). */
    iconUrl: string;
    /** The 256px variant for 2x displays; absent for SVG and default icons, and for bitmap icons
     *  whose 256px variant doesn't exist yet. */
    iconUrl2x?: string;
    /** Render the icon as a circle (e.g. YouTube channel avatars) rather than a square. */
    circularIcon?: boolean;
    /** What a typical post carries, as shown on the feed tile ('full posts', 'summaries', 'videos',
     *  'audio', ...; see utils/post-stats-badges.ts). Empty when the feed hasn't been analyzed yet. */
    types: string[];
    /** Average posts per day. Absent for feeds added to the catalog less than 48h ago. */
    postFrequency?: number;
    /** When Downread last saw a new post (ms since epoch, server time; as of the content's
     *  generation, so up to a day old). Absent for feeds without posts. */
    lastPostMs?: number;
    /** Whether the feed is mostly a paid subscription service. */
    paid: boolean;
}

// CDN file home/category/{slug}-{YYYYMMDD}.json: one Home category with ALL its feeds (the "See all"
// page). Written before home/page whenever the day's categories are (re)built.
export interface HomeCategoryContent {
    category: HomeCategory;
    /** Timestamp when this content was generated (ISO 8601 string) */
    lastUpdated: string;
    /** Date in YYYYMMDD format (Eastern timezone), same as the home/page it belongs to */
    date: string;
}

/** Global Downread vitality stats, refreshed hourly by the home content job. */
export interface HomeStats {
    /** Total number of feeds in Downread (DB COUNT over feeds). */
    feeds: number;
    /** Total number of posts across all feeds (DB SUM of postCount). */
    posts: number;
    /** New posts added since the previous hourly run (>= 0). */
    postsLastHour: number;
}

// stuff 'image-collection'
export interface ImageCollection {
    images: {
        name: string;
        url: string;
    }[];
}

// GET /api/admin/notifications
export interface RestAdminNotifications {
    feedsToProcess: number;
    /** Number of distinct discover feeds that became inactive within the last 7 days
     *  (inactiveSince < 7d old). Drives the "Inactive feeds" admin badge. */
    recentlyInactiveFeeds: number;
    /** Pending content reports awaiting admin review (abuse / illegal-content flags). */
    reportsToReview: number;
    /** Subset of reportsToReview that are legally time-sensitive (CSAM). */
    urgentReports: number;
}

// Content reports (abuse / illegal-content flagging) ------------------------

export const REPORT_REASONS = ['COPYRIGHT', 'CSAM', 'SEXUAL', 'ILLEGAL', 'SPAM', 'OWNERSHIP', 'TECHNICAL', 'OTHER'] as const;
export type ReportReason = typeof REPORT_REASONS[number];

export const REPORT_STATUSES = ['PENDING', 'DISMISSED', 'ACTIONED'] as const;
export type ReportStatus = typeof REPORT_STATUSES[number];

/** Reasons that are legally time-sensitive and must jump the review queue. */
export const URGENT_REPORT_REASONS: ReportReason[] = ['CSAM'];

/** Queue ordering for the admin report review queue: urgent reasons first ('0'), then
 *  oldest-first by creation time. Pure function — the API builds this into a FeedReport's
 *  uiSortKey_queue before persisting (now via the sync server). */
export function computeReportQueueKey(reason: ReportReason, createdAtIso: string, id: string): string {
    return `${URGENT_REPORT_REASONS.includes(reason) ? '0' : '1'}_${createdAtIso}_${id}`;
}

// POST /api/report - submit a report (public; optional auth, reporter may be logged out)
export interface RestContentReport {
    feedId: string;
    /** Present when reporting a specific post rather than the whole feed. */
    postIndex?: number;
    reason: ReportReason;
    /** Optional free-text detail from the reporter. */
    details?: string;
    /** Optional contact email, mainly for anonymous reporters (a DSA notice may include contact info). */
    reporterEmail?: string;
    /** Cloudflare Turnstile token; required for anonymous submissions once enabled. */
    turnstileToken?: string;
}

// Admin view of a stored report. Reporter PII (details/reporterUserId/reporterEmail) lives on the
// API instance named by `instance`; it is only present when this response was served by that
// instance (complete=true). For cross-instance reports the admin client fetches the PII separately
// from that instance (GET /api/admin/reports/personal) and merges it client-side.
export interface RestReport {
    id: string;
    feedId: string;
    postIndex?: number;
    reason: ReportReason;
    details?: string;
    reporterUserId?: string;
    reporterEmail?: string;
    reporterIp?: string;
    status: ReportStatus;
    createdAt: string;
    reviewedAt?: string;
    reviewedByUserId?: string;
    adminNotes?: string;
    /** Whether this report's reason is legally time-sensitive (CSAM). */
    urgent: boolean;
    /** API instance id (e.g. '01') holding this report's reporter PII. */
    instance: string;
    /** True when reporter PII was merged in by the serving instance (it owns this report). */
    complete: boolean;
}

// GET /api/admin/reports/personal?ids=a,b,c — reporter PII for reports owned by the queried
// instance. Returned only for ids whose personal row exists in that instance's local user DB.
export interface RestReportPersonal {
    id: string;
    details?: string;
    reporterUserId?: string;
    reporterEmail?: string;
    reporterIp?: string;
}

// PUT /api/admin/report/:id
export interface RestReportUpdate {
    status: ReportStatus;
    adminNotes?: string;
}


// --- Partner API (/api/partner/*): server-to-server calls of partner services (Yellowfeeds), authorized
// by a partner token (secrets.partnerTokens). Partners act for their own users, whom Downread never
// learns about: no user ids, names or emails go in or out.

/** A feed as partners see it: an existing Downread feed (id set) or one just fetched (no id yet). */
export interface RestPartnerFeed {
    /** Downread feed id; absent for a feed that is not in Downread yet */
    id?: string;
    /** Feed URL (after redirects for a new feed) */
    url: string;
    title: string;
    siteUrl?: string;
    /** The feed's own description element (plain text) */
    description?: string;
    /** The directory listing's description, if the feed is listed */
    catalogDescription?: string;
    category?: string;
    language?: string;
    /** Icon URL (192px); only for existing feeds */
    iconUrl?: string;
    /** True if the feed has an active directory (catalog) listing */
    listed: boolean;
    /** The listing's badges measured from the feed (as RestDirectoryFeed.badges); only for existing feeds */
    badges?: string[];
    /** Posts per day (as RestDirectoryFeed.postFrequency); only for feeds listed for a while */
    postFrequency?: number;
    /** What the directory may show of the posts; only for listed (or hidden) feeds, unset = 'excerpt' */
    previewMode?: PreviewMode;
}

// POST /api/partner/feed/lookup  body RestRequestFeedByUrl
/** existing: already a public Downread feed. new: fetched fine, not in Downread yet (POST
 *  /api/partner/feed/add creates it). fetching: still loading, ask again in a few seconds. bad: no
 *  usable feed at this URL (message says why). */
export type RestPartnerFeedLookup =
    | { status: 'existing' | 'new'; feed: RestPartnerFeed }
    | { status: 'fetching' }
    | { status: 'bad'; message: string };

// POST /api/partner/feed/add  body RestRequestFeedByUrl
export interface RestPartnerFeedAdded {
    feed: RestPartnerFeed;
    /** True if a new Downread feed was created (false: an existing one was found) */
    created: boolean;
}

// PUT /api/partner/catalog/:feedId
/** Sets a feed's directory listing for its publisher: the partner checked the ownership, and had
 *  what needs it reviewed by its admins (full posts, new entries). Absent fields stay as they are;
 *  null clears quote and description. Answers the feed as RestPartnerFeed (404 without a catalog
 *  entry, unless createIfMissing). */
export interface RestPartnerListing {
    title?: string;
    /** The tagline, quoted from the feed (the catalog entry's description) */
    quote?: string | null;
    /** The directory's text about the feed (catalogDescription) */
    description?: string | null;
    category?: string;
    /** A public URL of the new icon, downloaded into the feed's custom image */
    iconUrl?: string;
    /** 'hidden' deactivates the catalog entry; leaving 'hidden' activates it again (unless an admin
     *  had switched it off) */
    previewMode?: PreviewMode;
    /** Create the catalog entry when the feed has none (needs title and category): only for listings
     *  the partner's admins reviewed */
    createIfMissing?: boolean;
}
